<template>
  <Card v-loading="loading" class="stats-builder-filters">
    <div class="row">
      <div
        class="column stats-builder-filters__item large-3 medium-expand small-12"
      >
        <InputWithLabel :label="$t('APP.STATS_BUILDER.CHART_TYPE')">
          <ElCascader
            v-model="filterModel.primaryType"
            :options="types"
            size="large"
            @change="createReport"
          />
        </InputWithLabel>
      </div>
      <div
        class="column
      stats-builder-filters__item
       large-3 medium-expand small-12"
      >
        <InputWithLabel :label="$t('APP.STATS_BUILDER.RANGE')">
          <AlternativeSelect
            v-model="filterModel.range"
            :items="ranges"
            :placeholder="$t('APP.STATS_BUILDER.RANGE')"
            @change="createReport"
          />
        </InputWithLabel>
      </div>
      <div
        class="column
        stats-builder-filters__item
        stats-builder-filters__item--stick-end
        large-3 medium-expand small-12
        "
      >
        <DownloadCsvDropdown
          :primary-selected="primarySelectedType"
          :secondary-selected="secondarySelectedType"
          :range="filterModel.range"
        />
      </div>
    </div>

    <div class="row">
      <div class="column large-3 medium-expand small-12">
        <StatsBuilderExtra
          v-if="isPrimaryTypeSelected"
          v-model="filterModel.secondaryType"
          :options="types"
          @input="createReport"
        />
      </div>
    </div>
  </Card>
</template>

<script>
import InputWithLabel from '@@/common/InputWithLabel';
import StatsBuilderExtra from '@@/common/stats/StatsBuilderExtra';
import Card from '@@/common/Card';
import DownloadCsvDropdown from './DownloadCsvDropdown';
import packageName from '@/mixins/packageName';
import AlternativeSelect from '@@/common/AlternativeSelect';

function isSameInDeep(primary, secondary) {
  return (
    primary.type === secondary.type &&
    primary.child === secondary.child &&
    primary.tag === secondary.tag
  );
}
function isSameInType(primary, secondary) {
  return primary.type === secondary.type;
}

export default {
  name: 'StatsBuilderFilters',
  components: {
    Card,
    StatsBuilderExtra,
    InputWithLabel,
    DownloadCsvDropdown,
    AlternativeSelect,
  },
  mixins: [packageName],
  props: {
    eventBus: {
      required: true,
      type: Object,
    },
    types: {
      type: Array,
      default: () => [],
    },
    ranges: {
      type: Array,
      default: () => [],
    },
    defaultFilters: {
      type: Object,
      default: () => ({
        primaryType: [],
        secondaryType: [],
        range: '',
      }),
    },
    loading: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      filterModel: this.getDefaultFields(),
      flattedTableTags: {},
    };
  },
  computed: {
    isPrimaryTypeSelected() {
      return this.filterModel.primaryType.length;
    },
    primarySelectedType() {
      const { primaryType } = this.filterModel;
      if (!primaryType.length) {
        return {
          type: '',
          child: '',
          tag: '',
          tableTags: [],
        };
      }
      return primaryType[primaryType.length - 1];
    },
    secondarySelectedType() {
      const { secondaryType } = this.filterModel;
      if (!secondaryType.length) {
        return {
          type: '',
          child: '',
          tag: '',
          tableTags: [],
        };
      }
      return secondaryType[secondaryType.length - 1];
    },
  },

  watch: {
    defaultFilters() {
      this.setDefaultFilters(this.defaultFilters);
      this.createReport();
    },
  },

  mounted() {
    this.eventBus.$on('resetFields', () => {
      this.resetFields();
    });
  },
  methods: {
    createReport() {
      if (!this.isPrimaryTypeSelected) return;
      const { range } = this.filterModel;
      const { tableTags, ...primaryType } = this.primarySelectedType;
      const filters = {
        primaryType,
        range,
        tableTags,
      };

      const secondaryTypeWithTableTag = this.secondarySelectedType;
      if (secondaryTypeWithTableTag.type) {
        const {
          tableTags: secondaryTableTags,
          ...secondaryType
        } = secondaryTypeWithTableTag;
        if (!isSameInDeep(primaryType, secondaryType)) {
          filters.secondaryType = secondaryType;
        }
        if (!isSameInType(primaryType, secondaryType)) {
          filters.tableTags = tableTags.concat(secondaryTableTags);
        }
      }

      this.$emit('create', filters);
    },

    resetFields() {
      this.filterModel = this.getDefaultFields();
    },

    setDefaultFilters({ type, range }) {
      this.filterModel.primaryType = type;
      this.filterModel.range = range;
    },

    getDefaultFields() {
      return {
        primaryType: [],
        secondaryType: [],
        range: '',
      };
    },
  },
};
</script>

<style lang="scss">
.stats-builder-filters {
  &__image {
    position: absolute;
    width: 135px;
    opacity: 0.1;
    right: -10px;
    bottom: -26px;
    z-index: 10001;

    @include rtl() {
      right: auto;
      left: -10px;
    }
  }

  &__item {
    @include breakpoint(small only) {
      &:not(:last-child) {
        margin-bottom: $global-margin;
      }
    }

    &--stick-end {
      margin-left: auto;
      text-align: right;

      @include rtl() {
        margin-right: auto;
        margin-left: initial;
        text-align: left;
      }
      @include breakpoint(small only) {
        text-align: left;
        @include rtl() {
          text-align: right;
        }
      }
    }
  }

  &__select-box {
    padding: 0 rem-calc(25) 0 rem-calc(15);
  }
}

.row + .row {
  margin-top: $global-margin;
}
</style>
